/**
 * @fileoverview Memory Board Component
 * @description Grid of memory cards
 */

import React, { useMemo } from "react";
import { View, StyleSheet, Dimensions } from "react-native";
import { MemoryBoardProps, DIFFICULTY_CONFIGS } from "../types";
import { MemoryCard } from "./MemoryCard";

const SCREEN_WIDTH = Dimensions.get("window").width;
const BOARD_PADDING = 16;
const CARD_GAP = 8;

/**
 * MemoryBoard - Grid of flippable memory cards
 */
export const MemoryBoard: React.FC<MemoryBoardProps> = ({
  cards,
  revealedCards,
  disabled,
  gridSize,
  onCardPress,
}) => {
  const config = DIFFICULTY_CONFIGS[gridSize];

  /**
   * Calculate card size based on screen width and grid
   */
  const cardSize = useMemo(() => {
    const availableWidth = SCREEN_WIDTH - BOARD_PADDING * 2;
    const totalGaps = (config.cols - 1) * CARD_GAP;
    const cardMargins = config.cols * CARD_GAP; // margin on each side
    const size = (availableWidth - totalGaps - cardMargins) / config.cols;
    return Math.floor(size);
  }, [config.cols]);

  /**
   * Check if a card is revealed
   */
  const isCardRevealed = (cardId: string): boolean => {
    return revealedCards.includes(cardId);
  };

  /**
   * Check if a card is matched
   */
  const isCardMatched = (cardId: string): boolean => {
    const card = cards.find((c) => c.id === cardId);
    return card?.state === "matched";
  };

  /**
   * Render cards in rows
   */
  const renderRows = () => {
    const rows: React.ReactNode[] = [];
    
    for (let row = 0; row < config.rows; row++) {
      const rowCards = cards.slice(row * config.cols, (row + 1) * config.cols);
      
      rows.push(
        <View key={`row-${row}`} style={styles.row}>
          {rowCards.map((card) => (
            <MemoryCard
              key={card.id}
              card={card}
              isRevealed={isCardRevealed(card.id)}
              isMatched={isCardMatched(card.id)}
              disabled={disabled}
              onPress={() => onCardPress(card.id)}
              cardSize={cardSize}
            />
          ))}
        </View>
      );
    }
    
    return rows;
  };

  return (
    <View style={styles.container}>
      <View style={[styles.board, disabled && styles.boardDisabled]}>
        {renderRows()}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
    justifyContent: "center",
    padding: BOARD_PADDING,
  },
  board: {
    alignItems: "center",
  },
  boardDisabled: {
    opacity: 0.7,
  },
  row: {
    flexDirection: "row",
    justifyContent: "center",
  },
});

export default MemoryBoard;

